import { Activity, Gauge, Droplets, Info } from "lucide-react";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip";

const STATUS = {
  green: { label: "Nominal", dot: "bg-emerald-400", text: "text-emerald-300", ring: "border-emerald-800/50 bg-emerald-950/15" },
  amber: { label: "Degrading", dot: "bg-amber-400", text: "text-amber-300", ring: "border-amber-800/50 bg-amber-950/15" },
  red: { label: "Service due", dot: "bg-red-400", text: "text-red-300", ring: "border-red-800/50 bg-red-950/15" },
};

const InfoTip = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button className="text-slate-500 hover:text-cyan-300 transition-colors" aria-label="What this monitors">
        <Info className="h-3.5 w-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-[250px] bg-[#0b1120] border-slate-800 text-slate-300 text-xs leading-relaxed">
      {text}
    </TooltipContent>
  </Tooltip>
);

const Simulated = () => (
  <span className="mono text-[9px] uppercase tracking-widest text-slate-500 border border-slate-700/70 rounded px-1.5 py-0.5">
    Simulated
  </span>
);

const Card = ({ testid, icon: Icon, status, title, tip, value, valueLabel, rul }) => {
  const s = STATUS[status];
  const rulColor = rul > 66 ? "#10B981" : rul > 33 ? "#F59E0B" : "#EF4444";
  return (
    <div className={`rounded-xl border p-4 ${s.ring}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-300" />
          <span className="text-xs font-semibold text-slate-200">{title}</span>
        </div>
        <InfoTip text={tip} />
      </div>
      <div className="flex items-center justify-between mb-2.5">
        <span className={`inline-flex items-center gap-1.5 mono text-[10px] font-semibold ${s.text}`}>
          <span className={`h-2 w-2 rounded-full ${s.dot} beacon`} /> {s.label}
        </span>
        <Simulated />
      </div>
      {rul !== undefined ? (
        <div>
          <div className="flex justify-between mono text-[10px] text-slate-400 mb-1">
            <span>Remaining useful life</span>
            <span style={{ color: rulColor }}>{rul}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${rul}%`, backgroundColor: rulColor }}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className={`mono text-2xl font-extrabold ${s.text}`}>{value}</span>
          <span className="mono text-[10px] text-slate-500">{valueLabel}</span>
        </div>
      )}
    </div>
  );
};

export const HardwareReadiness = () => (
  <TooltipProvider delayDuration={100}>
    <div className="glass-panel rounded-2xl p-5" data-testid="hardware-readiness-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Hardware Readiness
        </h3>
        <span className="mono text-[10px] text-slate-500">Sensor health · reference indicators</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          testid="hw-ph-electrode"
          icon={Activity}
          status="green"
          title="pH Electrode Health"
          tip="In a live deployment this tracks the glass-membrane slope and zero-point offset of the pH probe. Electrode ageing shows up as gradual calibration drift and a slower response time."
          value="96%"
          valueLabel="slope retained"
        />
        <Card
          testid="hw-dosing-tubing"
          icon={Gauge}
          status="amber"
          title="Dosing Tubing Integrity"
          tip="Estimated remaining useful life of the peristaltic dosing tubing. Repeated roller compression fatigues the tubing wall, risking occlusion or rupture before its scheduled replacement."
          rul={62}
        />
        <Card
          testid="hw-do-optode"
          icon={Droplets}
          status="green"
          title="DO Optode Status"
          tip="Monitors the luminescence-lifetime signal of the dissolved-oxygen optode. Photobleaching of the sensing spot slowly reduces signal quality and measurement accuracy over time."
          value="99%"
          valueLabel="signal quality"
        />
      </div>
    </div>
  </TooltipProvider>
);
